/**
 * AuthProvider - manages JWT lifecycle, auto-refresh, and user state.
 * Practice UI variant: supports clinical roles (physician, nurse,
 * medical_assistant, front_desk, org_admin).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as authApi from "@/api/auth.api";
import { setTokens, clearTokens, getAccessToken, trySilentRefresh, csrfFromAccessToken } from "@/api/client";
import { clearCopilotChat } from "@/lib/copilot-chat-storage";
import { SessionTimeoutModal } from "@/components/auth/SessionTimeoutModal";
import { setAuthNotice } from "@/lib/toast";
import {
  IDLE_WARN_MS,
  IDLE_LOGOUT_MS,
  touchActivity,
  getIdleMs,
  resetIdleSession,
  setSessionWarningOpen as setIdleWarningOpen,
} from "@/lib/session-idle";
import { buildMsalConfig, entraLoginRequest, getMsalInstance } from "@/lib/msal-config";
import type { User, LoginRequest } from "@/types";

import {
  AuthContext,
  CLINICAL_ROLES,
  isClinicalRole,
  filterPracticeOrgOptions,
  type AuthContextValue,
  type ClinicalRole,
  type LoginStepResult,
} from "@/providers/auth-context";

export type { AuthContextValue, ClinicalRole, LoginStepResult } from "@/providers/auth-context";


interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
  const [secondsUntilLogout, setSecondsUntilLogout] = useState(15 * 60);
  const didInitRef = useRef(false);

  const isAuthenticated = !!user;

  /** Derive clinical role from user. */
  const clinicalRole: ClinicalRole | null = useMemo(() => {
    if (!user) return null;
    return isClinicalRole(user.role) ? user.role : null;
  }, [user]);

  /** Check if the current user has one of the given roles. */
  const hasRole = useCallback(
    (...roles: ClinicalRole[]): boolean => {
      if (!clinicalRole) return false;
      return roles.includes(clinicalRole);
    },
    [clinicalRole],
  );

  /** Fetch the current user profile. */
  const refreshAuth = useCallback(async (): Promise<User | null> => {
    try {
      const profile = await authApi.getProfile();
      // Ensure the user has a valid clinical role for this UI
      if (!isClinicalRole(profile.role)) {
        console.warn(
          `User role "${profile.role}" is not a valid clinical role. Access may be restricted.`,
        );
      }
      setUser(profile);
      return profile;
    } catch {
      setUser(null);
      clearTokens();
      return null;
    }
  }, []);

  /** Apply login response - shared by password, MFA, and Entra flows. */
  const applyLoginResponse = useCallback((res: Awaited<ReturnType<typeof authApi.login>>) => {
    const csrf =
      res.csrf_token ??
      (res.access_token ? csrfFromAccessToken(res.access_token) : null);
    if (!res.user || !res.access_token || !csrf) {
      throw new Error("Invalid login response - missing token or user profile");
    }
    if (!isClinicalRole(res.user.role)) {
      throw new Error(
        `Access denied. Role "${res.user.role}" is not authorized for the Practice portal. ` +
          `Allowed roles: ${CLINICAL_ROLES.join(", ")}`,
      );
    }
    setTokens(res.access_token, csrf, res.session_id, res.refresh_token);
    setUser(res.user);
    touchActivity();
  }, []);

  /** Map API login/select-org response into a UI step result (or apply tokens). */
  const resolveLoginStep = useCallback(
    (res: Awaited<ReturnType<typeof authApi.login>>, authMode: "local" | "entra"): LoginStepResult => {
      if (res.org_selection_required && res.selection_token && res.organizations?.length) {
        const practiceOrgs = filterPracticeOrgOptions(res.organizations);
        if (practiceOrgs.length === 0) {
          throw new Error(
            "Your accounts are not authorized for the Practice portal. Use the Admin console if you are a platform administrator.",
          );
        }
        // If only one practice-eligible org remains, auto-select it below via caller.
        return {
          mfaRequired: false,
          orgSelectionRequired: practiceOrgs.length > 1,
          selectionToken: res.selection_token,
          organizations: practiceOrgs,
          authMode,
        };
      }

      if (res.mfa_setup_required && res.setup_token) {
        return {
          mfaRequired: false,
          mfaSetupRequired: true,
          setupToken: res.setup_token,
        };
      }

      if (res.mfa_required && res.mfa_token) {
        return {
          mfaRequired: true,
          mfaToken: res.mfa_token,
          mfaMethod: res.mfa_method,
          mfaMethods: res.mfa_methods,
          mfaEmailSent: res.mfa_email_sent,
          mfaSmsSent: res.mfa_sms_sent,
        };
      }

      applyLoginResponse(res);
      return { mfaRequired: false };
    },
    [applyLoginResponse],
  );

  /** Log in and store tokens. When MFA/org picker is required, returns step data. */
  const login = useCallback(
    async (req: LoginRequest): Promise<LoginStepResult> => {
      const res = await authApi.login(req);
      const step = resolveLoginStep(res, "local");

      // Single practice-eligible org after multi-org discovery - finish without picker.
      if (
        step.orgSelectionRequired === false &&
        step.selectionToken &&
        step.organizations?.length === 1
      ) {
        const only = step.organizations[0]!;
        const selected = await authApi.selectOrg(
          step.selectionToken,
          only.organization_id,
          "local",
        );
        return resolveLoginStep(selected, "local");
      }

      return step;
    },
    [resolveLoginStep],
  );

  /** Complete org picker selection. */
  const selectOrg = useCallback(
    async (
      selectionToken: string,
      organizationId: string,
      authMode: "local" | "entra" = "local",
    ): Promise<LoginStepResult> => {
      const res = await authApi.selectOrg(selectionToken, organizationId, authMode);
      return resolveLoginStep(res, authMode);
    },
    [resolveLoginStep],
  );

  /** Complete onboarding MFA setup and store tokens. */
  const completeMFASetup = useCallback(async (setupToken: string, code: string) => {
    const res = await authApi.completeMFASetup(setupToken, code);
    applyLoginResponse(res);
  }, [applyLoginResponse]);

  /** Complete MFA login: verify code and store tokens + user. */
  const completeMFA = useCallback(async (mfaToken: string, code: string) => {
    const res = await authApi.verifyMFA(mfaToken, code);
    applyLoginResponse(res);
  }, [applyLoginResponse]);

  /** Start Microsoft Entra redirect login. */
  const loginWithMicrosoft = useCallback(async () => {
    const sso = await authApi.getSSOConfig();
    if (!sso.entra_enabled) {
      throw new Error("Microsoft sign-in is not enabled");
    }
    const cfg = buildMsalConfig(sso.tenant_id, sso.client_id);
    const msal = getMsalInstance(cfg ?? undefined);
    if (!msal) {
      throw new Error("Microsoft sign-in isn't set up for this portal. Sign in with your email and password, or contact your administrator.");
    }
    await msal.initialize();
    await msal.loginRedirect(entraLoginRequest);
  }, []);

  /** Complete Entra login after redirect callback. May return an org picker step. */
  const completeEntraLogin = useCallback(async (idToken: string): Promise<LoginStepResult> => {
    const res = await authApi.loginWithEntra(idToken);
    const step = resolveLoginStep(res, "entra");

    if (
      step.orgSelectionRequired === false &&
      step.selectionToken &&
      step.organizations?.length === 1
    ) {
      const only = step.organizations[0]!;
      const selected = await authApi.selectOrg(
        step.selectionToken,
        only.organization_id,
        "entra",
      );
      const after = resolveLoginStep(selected, "entra");
      if (after.mfaRequired && after.mfaToken) {
        throw new Error("Unexpected MFA step after Microsoft sign-in");
      }
      return after;
    }

    if (step.mfaRequired && step.mfaToken) {
      throw new Error("Unexpected MFA step after Microsoft sign-in");
    }
    return step;
  }, [resolveLoginStep]);

  /** Log out, clear tokens, and reset state. */
  const logout = useCallback(async () => {
    setSessionWarningOpen(false);
    setIdleWarningOpen(false);
    const userId = user?.id;
    try {
      await authApi.logout();
    } catch {
      // Best-effort - still clear local state
    }
    clearCopilotChat(userId);
    clearTokens();
    setUser(null);
    setAuthNotice({
      title: "Signed out",
      description: "You have been signed out successfully.",
    });
    if (typeof window !== "undefined") window.location.href = "/login";
  }, [user?.id]);

  /** Reset idle timer (call on Stay signed in). */
  const resetIdleTimer = useCallback(() => {
    resetIdleSession();
    setSessionWarningOpen(false);
    setSecondsUntilLogout(Math.ceil((IDLE_LOGOUT_MS - IDLE_WARN_MS) / 1000));
    void trySilentRefresh();
  }, []);

  // Idle detection: reset lastActivity on user activity
  useEffect(() => {
    if (!user) return;
    const onActivity = () => { touchActivity(); };
    window.addEventListener("mousemove", onActivity);
    window.addEventListener("keydown", onActivity);
    window.addEventListener("click", onActivity);
    return () => {
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("click", onActivity);
    };
  }, [user]);

  // Session timeout: 15 min idle → show warning, 30 min idle → logout (HIPAA)
  useEffect(() => {
    if (!user) {
      setSessionWarningOpen(false);
      setIdleWarningOpen(false);
      return;
    }

    touchActivity();

    const tick = () => {
      const idle = getIdleMs();
      if (idle >= IDLE_LOGOUT_MS) {
        void logout();
        return;
      }
      if (idle >= IDLE_WARN_MS) {
        setSessionWarningOpen(true);
        setIdleWarningOpen(true);
        setSecondsUntilLogout(Math.ceil((IDLE_LOGOUT_MS - idle) / 1000));
      }
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [user, logout]);

  // On mount: restore session from HttpOnly refresh cookie when possible
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    void (async () => {
      if (!getAccessToken()) {
        await trySilentRefresh();
      }
      if (getAccessToken()) {
        await refreshAuth();
      }
      setIsLoading(false);
    })();
  }, [refreshAuth]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      clinicalRole,
      hasRole,
      login,
      selectOrg,
      completeMFA,
      completeMFASetup,
      loginWithMicrosoft,
      completeEntraLogin,
      logout,
      refreshAuth,
    }),
    [user, isAuthenticated, isLoading, clinicalRole, hasRole, login, selectOrg, completeMFA, completeMFASetup, loginWithMicrosoft, completeEntraLogin, logout, refreshAuth],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutModal
        open={sessionWarningOpen}
        onStaySignedIn={resetIdleTimer}
        onLogout={logout}
        secondsUntilLogout={secondsUntilLogout}
      />
    </AuthContext.Provider>
  );
}
